import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppDataKeyField } from '@/form/AppDataKeyField';
import { AppInputField, AppSelectField } from '@/form/FormUI';
import type { IfNodeType, NodeFormProps } from '@/nodes';
import { useSavedFormValues } from '@/state';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { IfDataSchema, type IfDataType } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof IfDataSchema>;

const IfForm: FC<NodeFormProps<IfNodeType>> = ({ flowId, node, updateNode, cancelUpdate }) => {
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, IfDataType>({
        resolver: zodResolver(IfDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: IfDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors<FormInput>) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppDataKeyField
                        control={form.control}
                        name="field"
                        label="Data Key"
                        placeholder="field_name"
                        description={`The data key to check (set from previous nodes) or a referenced field such as "{{hvac.equipment.air-filter.modelNumber}}" or "{{electrical.info.gfci.value}}"`}
                        mode="consume"
                        currentNodeId={node.id}
                    />

                    <AppSelectField
                        control={form.control}
                        name="fn"
                        label="Transform"
                        options={[
                            { label: 'Years Since', value: 'years-since' },
                            { label: 'Months Since', value: 'months-since' },
                            { label: 'Days Since', value: 'days-since' },
                        ]}
                        description="Optional: transform the field value before comparison (for date/year fields)."
                    />

                    <AppSelectField
                        control={form.control}
                        name="op"
                        label="Operator"
                        options={IfDataSchema.shape.op.options}
                    />

                    <AppInputField
                        label="Value"
                        placeholder="field_value"
                        control={form.control}
                        name="value"
                        description="The value to check."
                    />
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default IfForm;
